import TutorialGroupCard from '@/pages/mt4-mt5/components/TutorialGroupCard';
import type { TutorialGroup } from '@/utils/mt4mt5Tutorials';

type GuideSectionProps = {
  sectionId: string;
  platform: 'MT4' | 'MT5';
  fullName: string;
  title: string;
  subtitle: string;
  groups: TutorialGroup[];
  image: string;
  caption: string;
  highlights: string[];
  tone: 'primary' | 'accent';
};

export default function GuideSection({
  sectionId,
  platform,
  fullName,
  title,
  subtitle,
  groups,
  image,
  caption,
  highlights,
  tone,
}: GuideSectionProps) {
  const isMt5 = platform === 'MT5';
  const total = groups.reduce((sum, group) => sum + group.articles.length, 0);

  const eyebrowClass =
    tone === 'primary'
      ? 'border-primary-200 bg-background-50 text-primary-700'
      : 'border-accent-200 bg-background-50 text-accent-700';

  const aside = (
    <aside className="self-start lg:sticky lg:top-24">
      <figure className="overflow-hidden rounded-xl border border-background-200 bg-background-50 p-2.5">
        <div className="flex items-center justify-between px-1 pb-2">
          <span
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
              tone === 'primary' ? 'bg-primary-500 text-background-50' : 'bg-accent-500 text-background-50'
            }`}
          >
            {platform}
          </span>
          <span className="text-[10px] text-foreground-400">{fullName}</span>
        </div>
        <div className="h-[190px] w-full overflow-hidden rounded-lg bg-background-100 md:h-[215px]">
          <img
            src={image}
            alt={`${caption} สำหรับคู่มือการใช้งาน ${platform}`}
            title={`${fullName} คู่มือภาษาไทย - Forex Academy`}
            className="h-full w-full object-cover object-top"
          />
        </div>
        <figcaption className="px-1 pt-2 text-[10px] text-foreground-500">{caption}</figcaption>
      </figure>

      <div className="mt-4 rounded-xl border border-background-200 bg-background-50 p-4">
        <p className="flex items-center gap-2 text-xs font-semibold text-foreground-800">
          <i className="ri-bookmark-3-line text-base text-primary-600" />
          ในคู่มือ {platform} มีอะไรบ้าง
        </p>
        <ul className="mt-3 flex flex-col gap-2.5">
          {highlights.map((item) => (
            <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
              <i className="ri-check-line mt-0.5 shrink-0 text-primary-600" />
              {item}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
          <span className="text-xs text-foreground-500">บทความทั้งหมด</span>
          <span className="font-num text-lg font-semibold text-foreground-950">{total}</span>
        </div>
      </div>
    </aside>
  );

  return (
    <section
      id={sectionId}
      className={`scroll-mt-24 border-b border-background-200 ${
        isMt5 ? 'bg-primary-50/60' : 'bg-background-50'
      }`}
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${eyebrowClass}`}
            >
              <i className="ri-graduation-cap-line text-sm" />
              {platform} · {total} บทความ
            </span>
            <h2 className="mt-4 text-balance font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-[34px]">
              {title}
            </h2>
            <p className="mt-3.5 text-sm leading-relaxed text-foreground-600 md:text-base">{subtitle}</p>
          </div>

          <a
            href="#platforms"
            className="inline-flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-xs font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-arrow-up-line text-base" />
            กลับไปเลือกแพลตฟอร์ม
          </a>
        </div>

        {isMt5 && (
          <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { icon: 'ri-time-line', label: '21 กรอบเวลา', note: 'ละเอียดกว่า MT4 ถึง 12 กรอบ' },
              { icon: 'ri-pulse-line', label: '38 อินดิเคเตอร์', note: 'มีเครื่องมือวิเคราะห์ในตัวมากกว่า' },
              { icon: 'ri-exchange-dollar-line', label: '8 ประเภทคำสั่ง', note: 'เพิ่มคำสั่ง Stop Limit ไว้รอราคา' },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 rounded-lg border border-accent-200/70 bg-background-50 px-4 py-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-base`} />
                </span>
                <div className="min-w-0">
                  <p className="font-heading text-sm font-semibold text-foreground-950">{item.label}</p>
                  <p className="text-[11px] leading-snug text-foreground-500">{item.note}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          className={`mt-8 grid grid-cols-1 gap-6 lg:gap-8 ${
            isMt5 ? 'lg:grid-cols-[1fr_320px]' : 'lg:grid-cols-[320px_1fr]'
          }`}
        >
          <div className={`flex min-w-0 flex-col gap-4 ${isMt5 ? 'lg:order-1' : 'lg:order-2'}`}>
            {groups.map((group, index) => (
              <TutorialGroupCard key={group.key} group={group} index={index} tone={tone} />
            ))}
          </div>
          <div className={isMt5 ? 'lg:order-2' : 'lg:order-1'}>{aside}</div>
        </div>
      </div>
    </section>
  );
}